'use client'

import { Client } from '@/lib/api/clients/client-api'
import { Allergy } from '@/lib/api/prontuario/prontuario-api'
import { createContext, ReactNode, useContext } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

export interface ProntuarioContext {
    client: Client
    allergies: Allergy[]
    reloadAllergies: () => Promise<void>
}

/**
 * Navegação e parâmetros de rota QUANDO as telas do prontuário rodam dentro do
 * modal, onde não existe rota nenhuma.
 */
export interface ProntuarioModalContext extends ProntuarioContext {
    /**
     * Recebe o mesmo argumento de `navigate()`. Caminhos dentro do prontuário deste
     * paciente trocam o conteúdo do modal; o resto sai para uma nova aba, porque a
     * tela de trás é o atendimento em andamento e o médico não pode perdê-la.
     */
    navigate: (to: string | number) => void
    /** `evolucaoId`, `anamneseId` etc. — o que viria de `useParams()` na rota. */
    params: Record<string, string | undefined>
}

/**
 * Mesmo conteúdo do contexto do `<Outlet>`, para quando as telas do prontuário são
 * renderizadas FORA da rota — dentro do modal aberto pelo menu "Prontuário" nas
 * telas de consulta, exame e procedimento.
 *
 * Um `<MemoryRouter>` dentro do modal seria o caminho óbvio para reaproveitar as
 * páginas sem tocá-las, mas o React Router recusa Router aninhado. Daí um contexto
 * próprio, lido pelos mesmos hooks que leem o do Outlet.
 */
const ModalProntuarioContext = createContext<ProntuarioModalContext | null>(null)

export function ProntuarioDataProvider({
    value,
    children,
}: {
    value: ProntuarioModalContext
    children: ReactNode
}) {
    return (
        <ModalProntuarioContext.Provider value={value}>
            {children}
        </ModalProntuarioContext.Provider>
    )
}

/**
 * Dados do paciente para as telas do prontuário, venham elas da rota (Outlet) ou do
 * modal. `clientId` sai do próprio paciente carregado — no modal não há parâmetro
 * de rota para ler.
 */
export function useProntuarioData(): ProntuarioContext & { clientId: string } {
    const fromModal = useContext(ModalProntuarioContext)
    const fromOutlet = useOutletContext<ProntuarioContext | null>()
    const params = useParams<{ clientId?: string }>()

    const data = fromModal ?? fromOutlet

    return {
        client: data?.client as Client,
        allergies: data?.allergies ?? [],
        reloadAllergies: data?.reloadAllergies ?? (async () => {}),
        clientId: String(data?.client?.id ?? params.clientId ?? ''),
    }
}

/**
 * `useNavigate()` das telas do prontuário.
 *
 * Na rota, é o navigate de sempre. No modal, quem resolve é o modal: "Nova
 * evolução" e "ver anamnese" trocam o conteúdo ali mesmo, em vez de tirar o médico
 * da consulta que ele está atendendo.
 */
export function useProntuarioNavigate(): (to: string | number) => void {
    const fromModal = useContext(ModalProntuarioContext)
    const routerNavigate = useNavigate()

    if (fromModal) return fromModal.navigate

    return (to: string | number) => {
        if (typeof to === 'number') {
            routerNavigate(to)
            return
        }
        routerNavigate(to)
    }
}

/**
 * `useParams()` das telas do prontuário: no modal os ids vêm do contexto, porque
 * não há rota para lê-los.
 */
export function useProntuarioParams<
    T extends Record<string, string | undefined>,
>(): T {
    const fromModal = useContext(ModalProntuarioContext)
    const routerParams = useParams()

    return (fromModal ? fromModal.params : routerParams) as T
}
